<template>
  <div
    class="quantity-selector"
    :class="[
      $blockClass(blockClass, 'quantity-selector'),
      {
        'quantity-selector--large': large,
        [$class('error')]: state === 'error',
      },
    ]"
  >
    <div
      class="
        quantity-selector__control
        quantity-selector__control--button
        quantity-selector__control--button-decrease
      "
      :class="$blockClass(blockClass, 'quantity-control')"
    >
      <button
        class="quantity-selector__button"
        :class="[
          $blockClass(blockClass, 'quantity-button'),
          { [$class('disabled')]: disableDecrease },
        ]"
        type="button"
        :aria-label="$string('cart.line_item.decrease_quantity')"
        @click="handleQuantityUpdate('decrease')"
      >
        <icon-minus />
      </button>
    </div>

    <div
      class="quantity-selector__control quantity-selector__control--input"
      :class="$blockClass(blockClass, 'quantity-control')"
    >
      <input
        class="quantity-selector__input text-body-s"
        :class="$blockClass(blockClass, 'quantity-input')"
        :name="name"
        pattern="[0-9]*"
        readonly
        tabindex="-1"
        type="number"
        :value="quantity"
        :aria-label="$string('cart.line_item.quantity')"
      >
    </div>

    <div
      class="
        quantity-selector__control
        quantity-selector__control--button
        quantity-selector__control--button-increase
      "
      :class="$blockClass(blockClass, 'quantity-control')"
    >
      <button
        class="quantity-selector__button"
        :class="[
          $blockClass(blockClass, 'quantity-button'),
          { [$class('disabled')]: disableIncrease },
        ]"
        type="button"
        :aria-label="$string('cart.line_item.increase_quantity')"
        @click="handleQuantityUpdate('increase')"
      >
        <icon-plus />
      </button>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Quantity selector
 * -----------------------------------------------------------------------------
 * Base template and functionality for quantity selector.
 * - Used in product form and line item.
 * - Responsible for emitting events to update quantity, parent components must
 *   action the update.
 *
 * @param {String} [blockClass] - Block level class to apply to all elements.
 * @param {Boolean} [large] - Large styles.
 * @param {Object} [item] - Line item object.
 * @param {Number} quantity - Actual line item quantity selector value as
 * reflected in Shopify's data.
 * @param {String} state - Parent component state.
 *
 */
import IconMinus from '~icons/misc/minus.svg'
import IconPlus from '~icons/misc/plus.svg'

export default {
  name: 'QuantitySelector',

  components: {
    IconMinus,
    IconPlus,
  },

  props: {
    blockClass: {
      type: String,
      default: '',
    },
    large: {
      type: Boolean,
      default: false,
    },
    item: {
      type: [Boolean, Object],
      default: false,
    },
    allowZero: {
      type: Boolean,
      default: true,
    },
    quantity: {
      type: Number,
      default: 1,
      required: true,
    },
    state: {
      type: String,
      default: 'loading',
      required: true,
    },
  },

  emits: {
    'update-quantity': (payload) => typeof payload === 'number',
  },

  data() {
    return {
      localQuantity: 1,
    }
  },

  computed: {

    /**
     * Compute unavailable.
     * - If object has avaiable = false and no quantity, means it's unavailable
     *   on the product page.
     * @returns {Boolean}
     */
    unavailable() {
      if (!this.item) {
        return false
      }

      return this.item.available === false && !this.item.quantity
    },

    /**
     * Compute if decrease button should be disabled.
     * @return {Boolean}
     */
    disableDecrease() {
      return (
        this.unavailable ||
        this.state === 'updating' ||
        this.state === 'loading'
      )
    },

    /**
     * Compute if increase button should be disabled.
     * @return {Boolean}
     */
    disableIncrease() {
      const outOfInventory =
        this.item.inventory_management === 'shopify' &&
        this.item.inventory_policy === 'deny' &&
        this.quantity >= this.inventory

      return (
        this.unavailable ||
        this.state === 'updating' ||
        this.state === 'loading' ||
        outOfInventory
      )
    },

    /**
     * Compute inventory.
     * - Returns 0 if no limit.
     * @returns {Number}
     */
    inventory() {
      return this.item?.inventory_quantity || 0
    },

    /**
     * Compute input name.
     * @return {String}
     */
    name() {
      if (this.item?.key) {
        return 'updates[]'
      }

      return 'quantity'
    },
  },

  watch: {

    /**
     * Watch actual quantity to update local quantity.
     */
    quantity() {
      this.localQuantity = this.quantity
    },
  },

  mounted() {
    this.localQuantity = this.quantity || 1
  },

  methods: {

    /**
     * Handle quantity selector update.
     * - Buttons only update local quantity which emits an event.
     * - If the event is successful then the quantity prop is updated and this
     *   is used for front-end display.
     * @param {String} type - `decrease` or `increase`.
     */
    handleQuantityUpdate(type) {
      if (
        this.state === 'updating' ||
        (type === 'decrease' && this.disableDecrease) ||
        (type === 'increase' && this.disableIncrease)
      ) {
        return
      }

      if (type === 'decrease') {
        if (this.localQuantity > 1 || this.allowZero) {
          this.localQuantity--
        }
      } else {
        this.localQuantity++
      }

      this.$emit('update-quantity', this.localQuantity)
    },
  },
}
</script>

<style lang="scss">
@import './quantity-selector';
</style>
